import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
This is the **NewsRPM RSS Feed Specification**, version 1.0. It describes the RSS format CloudQuote's news ingestion pipeline accepts when you want CloudQuote to consume articles from a feed you publish.

> **Note:** this is the *publisher* side of NewsRPM — how to get your articles into the system. To *retrieve* articles, see the [NewsRPM News API](/docs/newsrpm-api).

## Feed format support

NewsRPM accepts **RSS 2.0** feeds with an \`<rss>\` root element and a \`<channel>\` containing \`<item>\` elements.

## Required elements

Every \`<item>\` must include all three of:

| Element | Description |
| :---- | :---- |
| \`<title>\` | Article headline (max 255 characters). |
| \`<link>\` | Canonical URL to the article. |
| \`<pubDate>\` | Publication date in **RFC 822** (e.g., \`Wed, 15 Jan 2024 14:30:00 GMT\`). |

Articles missing any of these fields are rejected at ingestion time.

\`<dc:date>\` (Dublin Core, ISO 8601) is accepted as a fallback for the date.

## Article versioning

NewsRPM supports versioning so corrections and updates can replace earlier copies of an article.

\`\`\`xml
<category domain="http://newsrpm.com/version">2</category>
\`\`\`

- **Version 0** (default): initial publication; no version tag required.
- **Version 1+**: triggers reprocessing of an existing article with the same identifier.
- Version numbers must be **integers** and should only increase.

### Example: an updated article

\`\`\`xml
<item>
  <title>Company XYZ Reports Q4 Earnings — Updated</title>
  <link>https://example.com/article/xyz-earnings</link>
  <pubDate>Wed, 15 Jan 2024 14:30:00 GMT</pubDate>
  <category domain="http://newsrpm.com/version">1</category>
  <description>Updated with corrected revenue figures...</description>
</item>
\`\`\`

## Financial ticker tagging

Three methods are supported. **The first is recommended.**

### 1. FinancialContent stock symbol (recommended)

\`\`\`xml
<category domain="http://rss.financialcontent.com/stocksymbol">AAPL</category>
<category domain="http://rss.financialcontent.com/stocksymbol">MSFT</category>
\`\`\`

### 2. Company symbol (Dublin Core extension)

\`\`\`xml
<company:symbol xmlns:company="http://purl.org/rss/1.0/modules/company">AAPL</company:symbol>
\`\`\`

### 3. \`<stock_tickers>\` element

\`\`\`xml
<stock_tickers>AAPL,NASDAQ:AAPL</stock_tickers>
\`\`\`

### Format guidelines

- **Plain tickers:** \`AAPL\`, \`MSFT\`, \`GOOGL\`.
- **Exchange-prefixed:** \`NASDAQ:AAPL\`, \`NYSE:IBM\`, \`TSX:SHOP\`.
- **International exchanges:** \`LSE:VOD\`, \`ASX:BHP\`, \`TSE:7203\`.

NewsRPM normalizes ticker symbols, maps them to the right exchanges, validates them against market databases, and generates prefixed variants automatically.

## Content body delivery

Three options, in increasing order of editorial control:

### Method 1: \`<description>\` (basic)

\`\`\`xml
<description><![CDATA[
  <p>Article content goes here…</p>
]]></description>
\`\`\`

Use for short summaries or feeds that include full content inline.

### Method 2: \`<content:encoded>\` (full HTML)

\`\`\`xml
<content:encoded xmlns:content="http://purl.org/rss/1.0/modules/content/"><![CDATA[
  <article>
    <p>Full article content with HTML formatting…</p>
  </article>
]]></content:encoded>
\`\`\`

### Method 3: linked page (recommended for full articles)

Provide just the \`<link>\` and let NewsRPM fetch the page:

1. Fetches the linked URL.
2. Extracts content using XPath selectors configured for your source.
3. Processes embedded images and media.
4. Reads metadata from page headers (Open Graph, Twitter Cards, Schema.org).

## Image handling

### Primary image

\`\`\`xml
<media:thumbnail
  xmlns:media="http://search.yahoo.com/mrss/"
  url="https://example.com/images/article-photo.jpg" />
\`\`\`

### Requirements

- **Formats:** JPEG, PNG, GIF, WebP.
- **Recommended size:** 1200×630 (Open Graph standard).
- **Max file size:** 1 MB.
- **URL:** absolute, HTTPS preferred.

Only the **first** \`<media:thumbnail>\` is used as the primary image. Additional images should be inline in the article body.

### Linked-page extraction

When NewsRPM fetches the linked URL, it also pulls Open Graph images, Twitter Card images, Schema.org image metadata, and the first significant image in the article body — falling back through that list in order.

## Metadata extraction

### Summary priority

NewsRPM extracts the article summary from these sources, in order:

1. Open Graph description (from the linked page).
2. \`<meta name="description">\` (from the linked page).
3. \`<description>\` (from the feed).

### First paragraph

When processing a linked page, the first \`<p>\` of the article body is extracted automatically and stored in two forms — plain text and formatted HTML.

### Author

Both formats are accepted:

\`\`\`xml
<author>editor@example.com (John Doe)</author>
<dc:creator xmlns:dc="http://purl.org/dc/elements/1.1/">John Doe</dc:creator>
\`\`\`

### Language

\`\`\`xml
<dc:language xmlns:dc="http://purl.org/dc/elements/1.1/">en-US</dc:language>
\`\`\`

Accepts ISO 639-1 (\`en\`, \`fr\`, \`de\`, \`es\`) and ISO 639-1 with region (\`en-US\`, \`en-GB\`, \`fr-CA\`).

### GUID

\`\`\`xml
<guid isPermaLink="false">unique-article-id-12345</guid>
\`\`\`

Used for deduplication and update tracking. Once you assign a GUID to an article, keep using the same GUID for any updates to that article.

## Visibility control

\`\`\`xml
<category domain="http://newsrpm.com/visibility">published</category>
\`\`\`

| Value | Description |
| :---- | :---- |
| \`published\` | Publicly visible (default). |
| \`retracted\` | Article is retracted. |

## Tag reference

| Element | Required | Purpose |
| :---- | :---- | :---- |
| \`<title>\` | **Yes** | Headline |
| \`<link>\` | **Yes** | URL |
| \`<pubDate>\` | **Yes** | Date |
| \`<description>\` | No | Summary or body |
| \`<content:encoded>\` | No | Full HTML body |
| \`<guid>\` | No | Unique identifier |
| \`<author>\` | No | Author |
| \`<category>\` | No | Categories and metadata |
| \`<media:thumbnail>\` | No | Primary image |
| \`<dc:creator>\` | No | Author (Dublin Core) |
| \`<dc:date>\` | No | Date (Dublin Core) |
| \`<dc:language>\` | No | Language code |
| \`<company:symbol>\` | No | Stock ticker |
| \`<stock_tickers>\` | No | Comma-separated tickers |

### NewsRPM custom domains

| Extension | Format | Purpose |
| :---- | :---- | :---- |
| Version | \`<category domain="http://newsrpm.com/version">N</category>\` | Article versioning |
| Visibility | \`<category domain="http://newsrpm.com/visibility">STATUS</category>\` | Publication status |
| Stock symbol | \`<category domain="http://rss.financialcontent.com/stocksymbol">TICKER</category>\` | Financial ticker |

## Complete example

\`\`\`xml
<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0"
     xmlns:content="http://purl.org/rss/1.0/modules/content/"
     xmlns:dc="http://purl.org/dc/elements/1.1/"
     xmlns:media="http://search.yahoo.com/mrss/"
     xmlns:company="http://purl.org/rss/1.0/modules/company">
  <channel>
    <title>Example Financial News</title>
    <link>https://example.com</link>
    <description>Latest financial news and analysis</description>
    <language>en-US</language>

    <item>
      <title>Apple Reports Record Q4 Earnings, Stock Surges</title>
      <link>https://example.com/articles/apple-q4-earnings-2024</link>
      <guid isPermaLink="false">apple-q4-2024-earnings-report</guid>
      <pubDate>Wed, 15 Jan 2024 14:30:00 GMT</pubDate>

      <author>news@example.com (Jane Smith)</author>
      <dc:creator>Jane Smith</dc:creator>

      <description><![CDATA[
        Apple Inc. announced record-breaking fourth quarter earnings today,
        beating analyst expectations and sending shares higher in after-hours trading.
      ]]></description>

      <content:encoded><![CDATA[
        <article>
          <p>Apple Inc. announced record-breaking fourth quarter earnings today...</p>
          <p>The tech giant reported revenue of $123.4 billion, up 8% year-over-year.</p>
        </article>
      ]]></content:encoded>

      <category domain="http://rss.financialcontent.com/stocksymbol">AAPL</category>
      <stock_tickers>AAPL,NASDAQ:AAPL</stock_tickers>
      <company:symbol>AAPL</company:symbol>

      <media:thumbnail url="https://example.com/images/apple-earnings-q4-2024.jpg" />

      <category domain="http://newsrpm.com/version">0</category>
      <category domain="http://newsrpm.com/visibility">published</category>

      <category>Earnings</category>
      <category>Technology</category>

      <dc:language>en-US</dc:language>
    </item>
  </channel>
</rss>
\`\`\`

## Best practices

### Content delivery

- **Use CDATA** for HTML content to avoid XML parsing issues.
- **Use absolute URLs** for all links and images.
- **Include a summary** even when providing full content.
- **Use HTTPS** for all URLs when possible.

### Ticker tagging

- **Use exchange prefixes** for international stocks (\`LSE:VOD\`).
- **Tag every mentioned ticker**, not just the primary subject.
- **Validate ticker symbols** before publishing.
- **Use one consistent ticker format** throughout your feed.

### Performance

- **Limit feed size** to 50–100 items.
- **Use GUIDs consistently** so deduplication and version tracking stay reliable.

### Versioning

- **Increment** the version when content changes.
- **Don't reuse** version numbers for different updates.
- **Use version 0** for initial publications (it's the default — you can omit the tag).

## Validation

Validate your feed against:

- [W3C Feed Validation Service](https://validator.w3.org/feed/)
- [RSS Board Validator](http://www.rssboard.org/rss-validator/)

For test ingestion, custom XPath selectors, ticker mapping, and metadata rules, [contact us](/contact).

## Required namespaces

\`\`\`xml
xmlns:content="http://purl.org/rss/1.0/modules/content/"
xmlns:dc="http://purl.org/dc/elements/1.1/"
xmlns:media="http://search.yahoo.com/mrss/"
xmlns:company="http://purl.org/rss/1.0/modules/company"
\`\`\`

### Custom NewsRPM domains

- Version: \`http://newsrpm.com/version\`
- Visibility: \`http://newsrpm.com/visibility\`
- Stock symbol: \`http://rss.financialcontent.com/stocksymbol\`
`;

export default function RssFeedsPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "rss-feeds")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
